
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { RunnitsImageSelectMixin } from '@/mixins';
import { getRunnitNodeRunResultFilePath } from '@run-diffusion/shared';
import { RunnitNodeRunResult, RunnitNodeRun } from '@run-diffusion/shared';
import { storage } from '@/firebase';
import {
	GALLERY_SELECTION_CONTEXT_KEY,
	isGalleryMultiSelection,
	isGallerySelectionMode,
	isGallerySingleSelection,
	isSelectedInGalleryContext,
} from '@/components/ImageGallery/gallerySelectionContext';

const WIDTH_XS_BREAKPOINT = 120;

export default Vue.extend({
	name: 'RunnitLayers',
	inject: {
		gallerySelectionContext: { from: GALLERY_SELECTION_CONTEXT_KEY, default: null },
	},
	mixins: [RunnitsImageSelectMixin],
	props: {
		nodeRunResult: {
			type: Object as () => RunnitNodeRunResult,
			required: true,
		},
		nodeRun: {
			type: Object as () => RunnitNodeRun | null,
			default: null,
		},
		upload: {
			type: Object,
			default: null,
		},
		avatar: {
			type: Object,
			default: null,
		},
		noClick: {
			type: Boolean,
			default: false,
		},
		isThumb: {
			type: Boolean,
			default: false,
		},
		isAvatar: {
			type: Boolean,
			default: false,
		},
		clearable: {
			type: Boolean,
			default: false,
		},
		width: {
			type: [String, Number],
			default: null,
		},
		height: {
			type: [String, Number],
			default: null,
		},
		maxHeight: {
			type: [String, Number],
			default: null,
		},
		isErrorPlaceholder: {
			type: Boolean,
			default: false,
		},
		isLoadingPlaceholder: {
			type: Boolean,
			default: false,
		},
		isSelectedImage: {
			type: Boolean,
			default: false,
		},
		allowFullScreen: {
			type: Boolean,
			default: false,
		},
		coldStartWarningSeconds: {
			type: Number,
			default: null,
		},
		enableComparison: {
			type: Boolean,
			default: false,
		},
		draggable: {
			type: Boolean,
			default: false,
		},
		isDragging: {
			type: Boolean,
			default: false,
		},
		timerStartAtMillis: {
			type: Number,
			default: null,
		},
		overrideBorderRadius: {
			type: String,
			default: null,
		},
	},
	data () {
		return {
			WIDTH_XS_BREAKPOINT,
			srcIsError: false,
			errorMessage: '',
			dimensions: {
				width: 0,
				height: 0,
			},
			displayFileSrc: null as string | null,
			srcIsFetching: false,

			// Lazy loading
			isVisible: false,
			observer: null,
		};
	},
	computed: {
		...mapState([
			'runnitState',
			'dragAndDrop',
		]),
		displayFileAlt (): string {
			return this.nodeRunResult?.displayFile?.name || 'Layers result';
		},
		computedWidth (): string | number | null {
			if (this.width) {
				return this.width;
			}
			return this.dimensions.width > 0 ? this.dimensions.width : null;
		},
		computedHeight (): string | number | null {
			if (this.height) {
				return this.height;
			}
			return this.dimensions.height > 0 ? this.dimensions.height : null;
		},
		clearText (): string {
			return 'Clear layers result';
		},
		isSelectionMode () {
			if (this.gallerySelectionContext) {
				return isGallerySelectionMode(this.gallerySelectionContext, 'LAYERS', this.noClick);
			}
			return !!(!this.noClick && this.currentSelectionState.nodeRunResultSelectionMode && this.currentSelectionState.canSelectTypes.includes('LAYERS'));
		},
		isSingleSelection () {
			if (this.gallerySelectionContext) {
				return isGallerySingleSelection(this.gallerySelectionContext);
			}
			return this.currentSelectionState.nodeRunResultSelectionMode === 'SINGLE';
		},
		isMultiSelection () {
			if (this.gallerySelectionContext) {
				return isGalleryMultiSelection(this.gallerySelectionContext);
			}
			return this.currentSelectionState.nodeRunResultSelectionMode === 'MULTIPLE';
		},
		isSelected () {
			if (this.gallerySelectionContext) {
				return isSelectedInGalleryContext(this.gallerySelectionContext, {
					nodeRunResultUuid: this.nodeRunResult?.uuid || null,
				});
			}
			return !!(
				this.currentSelectionState.selectedNodeRunResults &&
				this.currentSelectionState.selectedNodeRunResults.some((result: any) => result.uuid === this.nodeRunResult.uuid)
			);
		},
		useComparisonSlider () {
			return !!(this.runnitState && this.runnitState.imageComparisonMode);
		},
	},
	methods: {
		async fetchDisplayFileSrc (): Promise<void> {
			if (!this.nodeRunResult?.displayFile || this.srcIsFetching || !this.isVisible) {
				return;
			}

			try {
				this.srcIsFetching = true;
				this.srcIsError = false;
				this.errorMessage = '';

				const { filePath } = getRunnitNodeRunResultFilePath(this.nodeRunResult);
				const fileRef = storage.ref(filePath);
				this.displayFileSrc = await fileRef.getDownloadURL();
			} catch (error) {
				console.error('Error fetching display file URL:', error);
				this.srcIsError = true;
				this.errorMessage = 'Failed to load layers image';
			} finally {
				this.srcIsFetching = false;
			}
		},
		onClick (): void {
			if (this.noClick) {
				return;
			}
			// Selection mode owns the click. The selection overlay and the image body
			// share this handler, so without this branch the "+" affordance rendered
			// (isSelectionMode is true for LAYERS) but every click fell through to
			// on-click and opened the carousel instead. That is why LAYERS results
			// could be shown as selectable and still never enter a selection.
			// Mirrors RunnitImage.onClick and RunnitAsset3D.onClick.
			if (this.isSelectionMode) {
				const payload = {
					nodeRunResult: this.nodeRunResult,
					upload: this.upload,
					avatar: this.avatar,
				};
				if (this.isSingleSelection) {
					this.$emit('on-single-select', payload);
				} else if (this.isMultiSelection) {
					this.$emit('on-multi-select', payload);
				}
				return;
			}
			this.$emit('on-click', this.nodeRunResult);
		},
		onClearClick (): void {
			this.$emit('on-clear', this.nodeRunResult);
		},
		onImageLoad (): void {
			this.srcIsError = false;
			this.errorMessage = '';
			this.updateDimensions();
		},
		onImageError (event: Event): void {
			console.error('Error loading layers image:', event);
			this.srcIsError = true;
			this.errorMessage = 'Failed to load layers image';
		},
		updateDimensions (): void {
			if (this.$refs.runnitLayersImage) {
				const img = this.$refs.runnitLayersImage as HTMLImageElement;
				this.dimensions = {
					width: img.naturalWidth,
					height: img.naturalHeight,
				};
				this.$emit('dimensions-updated', this.dimensions);
			}
		},
	},
	watch: {
		nodeRunResult: {
			handler () {
				if (this.isVisible) {
					this.fetchDisplayFileSrc();
				}
			},
			immediate: true,
		},
	},
	mounted () {
		this.updateDimensions();

		// Setup intersection observer for lazy loading
		this.observer = new IntersectionObserver(
			(entries) => {
				entries.forEach(entry => {
					if (entry.isIntersecting) {
						this.isVisible = true;
						// Start loading the display file
						if (this.nodeRunResult) {
							this.fetchDisplayFileSrc();
						}
						// Unobserve after first intersection
						this.observer.unobserve(entry.target);
					}
				});
			},
			{
				rootMargin: '500px 0px', // Start loading when within 500px of viewport
				threshold: 0.01 // Trigger when 1% of the element is visible
			}
		);

		// Start observing the container
		if (this.$refs.runnitLayersContainer) {
			this.observer.observe(this.$refs.runnitLayersContainer);
		}
	},
	beforeDestroy () {
		if (this.observer) {
			this.observer.disconnect();
		}
	},
});
